import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  MapPin,
  Map,
  X,
  Phone,
  Mail,
  Star,
  Save,
  Camera,
  Award,
  Briefcase,
  ArrowLeft,
  Upload,
  Settings,
  CheckCircle
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import MembershipGuard from '../common/MembershipGuard';
import { normalizeImageUrl } from '../../utils/api';
import axios from 'axios';
import ServiceLocationMap from './ServiceLocationMap';
import './TalentProfileEdit.css';

interface TalentProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  parish: string;
  bio: string;
  profilePicture?: string;
  profilePictureFile?: File;
  coverImage?: string;
  coverImageFile?: File;
  skills: Array<{ name: string, experience_level: string }>;
  yearsOfExperience: number;
  hourlyRate: number;
  rating: number;
  totalReviews: number;
  completedJobs: number;
  responseTime: string;
  availability: string;
  joinDate: string;
  isVerified: boolean;
  portfolio: string[];
  certifications: string[];
  dateOfBirth?: string;
  gender?: string;
  verificationStatus: string;
  profileStatus: string;
  latitude?: number;
  longitude?: number;
  city?: string;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('skillseek_talent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const TalentProfileEdit: React.FC = () => {
  const navigate = useNavigate();
  const { talent } = useTalentAuth();
  const [profile, setProfile] = useState<TalentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const handleLocationConfirm = (lat: number, lng: number, address: string, city: string, parish: string) => {
    handleInputChange('latitude', lat);
    handleInputChange('longitude', lng);
    // Address, city, and parish are now separate and not auto-updated by confirming map location
    setShowLocationModal(false);
  };
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  const jamaicaParishes = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
    'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
    'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
  ];

  const availableSkills = [
    'Web Development', 'Mobile Development', 'Graphic Design', 'Digital Marketing',
    'Content Writing', 'Photography', 'Video Editing', 'Data Analysis',
    'Project Management', 'UI/UX Design', 'SEO', 'Social Media Management',
    'Accounting', 'Legal Services', 'Consulting', 'Translation',
    'Home Cleaning', 'Plumbing', 'Electrical Work', 'Carpentry',
    'Gardening', 'Pet Care', 'Tutoring', 'Music Lessons'
  ];

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/talent/auth/profile');

      if (response.data.success) {
        const talentData = response.data.talent;
        const transformedProfile: TalentProfile = {
          id: talentData.id.toString(),
          firstName: talentData.first_name || '',
          lastName: talentData.last_name || '',
          email: talentData.email || '',
          phone: talentData.phone || '',
          address: talentData.address || '',
          parish: talentData.parish || '',
          bio: talentData.bio || '',
          profilePicture: normalizeImageUrl(talentData.profile_picture, 'https://via.placeholder.com/150'),
          coverImage: normalizeImageUrl(talentData.cover_image, 'https://via.placeholder.com/1200x400'),
          skills: Array.isArray(talentData.skills) ? talentData.skills : [],
          yearsOfExperience: talentData.experience_years || 0,
          hourlyRate: talentData.hourly_rate || 0,
          rating: talentData.rating || 0,
          totalReviews: talentData.total_reviews || 0,
          completedJobs: talentData.total_jobs_completed || 0,
          responseTime: talentData.response_time || '2 hours',
          availability: talentData.profile_status === 'active' ? 'Available' : 'Unavailable',
          joinDate: talentData.created_at || '',
          isVerified: talentData.verification_status === 'verified',
          portfolio: talentData.portfolio || [],
          certifications: talentData.certifications || [],
          dateOfBirth: talentData.date_of_birth || '',
          gender: talentData.gender || '',
          verificationStatus: talentData.verification_status || 'pending',
          profileStatus: talentData.profile_status || 'active',
          latitude: talentData.latitude ? parseFloat(talentData.latitude) : undefined,
          longitude: talentData.longitude ? parseFloat(talentData.longitude) : undefined,
          city: talentData.city || ''
        };
        setProfile(transformedProfile);
      } else {
        throw new Error(response.data.message || 'Failed to fetch profile');
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (talent) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [talent, fetchProfile]);

  const handleInputChange = (field: keyof TalentProfile, value: any) => {
    setProfile(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleSkillToggle = (skill: string) => {
    if (!profile) return;
    const currentSkills = profile.skills || [];
    const skillExists = currentSkills.find(s => s.name === skill);
    const updatedSkills = skillExists
      ? currentSkills.filter(s => s.name !== skill)
      : [...currentSkills, { name: skill, experience_level: 'intermediate' }];
    setProfile({ ...profile, skills: updatedSkills });
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      setProfile(prev => prev ? { ...prev, profilePictureFile: file } : null);
      const reader = new FileReader();
      reader.onload = (event) => setImagePreview(event.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      setProfile(prev => prev ? { ...prev, coverImageFile: file } : null);
      const reader = new FileReader();
      reader.onload = (event) => setCoverImagePreview(event.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    try {
      setSaving(true);
      setError(null);

      let profilePicturePath = profile.profilePicture;
      let coverImagePath = profile.coverImage;

      // Extract path part for existing images
      const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      if (profilePicturePath?.startsWith(baseApiUrl)) {
        profilePicturePath = profilePicturePath.replace(baseApiUrl, '');
      }
      if (coverImagePath?.startsWith(baseApiUrl)) {
        coverImagePath = coverImagePath.replace(baseApiUrl, '');
      }

      // Upload profile picture if new file selected
      if (profile.profilePictureFile) {
        const formData = new FormData();
        formData.append('image', profile.profilePictureFile);
        const res = await api.post('/talent/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.success) profilePicturePath = res.data.filePath;
        else throw new Error(res.data.message || 'Failed to upload profile image');
      }

      // Upload cover image if new file selected
      if (profile.coverImageFile) {
        const formData = new FormData();
        formData.append('image', profile.coverImageFile);
        const res = await api.post('/talent/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.success) coverImagePath = res.data.filePath;
        else throw new Error(res.data.message || 'Failed to upload cover image');
      }

      const updateData = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        parish: profile.parish,
        gender: profile.gender,
        date_of_birth: profile.dateOfBirth,
        profile_picture: profilePicturePath,
        cover_image: coverImagePath,
        profile_status: profile.availability === 'Available' ? 'active' : 'inactive',
        latitude: profile.latitude,
        longitude: profile.longitude,
        city: profile.city,
        skills: profile.skills,
        experience_years: profile.yearsOfExperience
      };

      const response = await api.put('/talent/auth/profile', updateData);
      if (response.data.success) {
        navigate('/talent/profile');
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!talent) {
    return (
      <div className="profile-unauthorized">
        <div className="unauthorized-content">
          <h2>Access Denied</h2>
          <p>Please log in to edit your profile</p>
          <div className="unauthorized-actions">
            <button onClick={() => navigate('/talent/login')} className="login-btn">Log In</button>
            <button onClick={() => navigate('/talent/register')} className="register-btn">Register</button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="profile-error">
        <p>Error: {error}</p>
        <button onClick={fetchProfile} className="retry-button">Try Again</button>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <MembershipGuard>
      <div className="talent-profile-edit">
        <div className="edit-header">
          <button onClick={() => navigate('/talent/profile')} className="back-button">
            <ArrowLeft size={20} /> Back to Profile
          </button>
          <h1>Edit Profile</h1>
          <div className="header-status">
            <span className={`status-badge ${profile.verificationStatus}`}>
              {profile.verificationStatus === 'verified' && <CheckCircle size={16} />}
              {profile.verificationStatus === 'verified' ? 'Verified' : 'Pending Verification'}
            </span>
          </div>
        </div>

        <div className="edit-content">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => setError(null)} className="close-error">×</button>
            </div>
          )}

          <div className="edit-section cover-section">
            <h2><Camera size={20} /> Cover Image</h2>
            <div className="cover-image-edit">
              <div className="cover-image-container">
                <img
                  src={coverImagePreview || profile.coverImage || 'https://via.placeholder.com/1200x400'}
                  alt="Cover"
                  className="cover-image"
                />
                <div className="cover-image-overlay">
                  <label className="upload-button">
                    <Upload size={20} />
                    <span>Change Cover Image</span>
                    <input type="file" accept="image/*" onChange={handleCoverImageChange} hidden />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="edit-section profile-picture-section">
            <h2><User size={20} /> Profile Picture</h2>
            <div className="profile-picture-edit">
              <div className="current-picture">
                <img src={imagePreview || profile.profilePicture || 'https://via.placeholder.com/150'} alt="Profile" />
                <label className="picture-upload">
                  <Camera size={20} />
                  <input type="file" accept="image/*" onChange={handleProfileImageChange} hidden />
                </label>
              </div>
              <div className="picture-info">
                <h3>{profile.firstName} {profile.lastName}</h3>
                <p className="picture-help">Square image works best. Max 5MB.</p>
              </div>
            </div>
          </div>

          <div className="form-columns">
            <div className="form-column">
              <div className="edit-section">
                <h2><User size={20} /> Basic Information</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" value={profile.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)} className="form-input" required />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input type="text" value={profile.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)} className="form-input" required />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={profile.email} onChange={(e) => handleInputChange('email', e.target.value)} className="form-input" required />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" value={profile.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className="form-input" placeholder="+1 (876) 123-4567" />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input type="date" value={profile.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select value={profile.gender} onChange={(e) => handleInputChange('gender', e.target.value)} className="form-select">
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="edit-section">
                <h2><MapPin size={20} /> Address Information</h2>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label>Address</label>
                  <div className="input-with-icon" style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={profile.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="form-input"
                      placeholder="Enter your street address"
                      style={{ width: '100%', paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowLocationModal(true)}
                      className="map-trigger-btn"
                      title="Set coordinates on map"
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Map size={20} />
                    </button>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={profile.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="form-input"
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>Parish</label>
                    <select
                      value={profile.parish || ''}
                      onChange={(e) => handleInputChange('parish', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select Parish</option>
                      {jamaicaParishes.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="edit-section">
                <h2><Award size={20} /> Performance Stats</h2>
                <div className="performance-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                  <div className="stat-card">
                    <div className="stat-value">{parseFloat(profile.rating as any || '0').toFixed(1)}</div>
                    <div className="stat-label">Rating</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{profile.completedJobs}</div>
                    <div className="stat-label">Jobs Done</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{profile.totalReviews}</div>
                    <div className="stat-label">Reviews</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{profile.responseTime}</div>
                    <div className="stat-label">Response</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-column">
              <div className="edit-section">
                <h2><Award size={20} /> About You</h2>
                <div className="form-group">
                  <label>Professional Bio</label>
                  <textarea value={profile.bio} onChange={(e) => handleInputChange('bio', e.target.value)} className="form-textarea" rows={6} placeholder="Tell clients about yourself..." />
                  <small className="character-count">{profile.bio?.length || 0}/1000</small>
                </div>
              </div>

              <div className="edit-section">
                <h2><Briefcase size={20} /> Experience & Skills</h2>
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label>Years of Experience</label>
                  <input type="number" value={profile.yearsOfExperience} onChange={(e) => handleInputChange('yearsOfExperience', parseInt(e.target.value) || 0)} className="form-input" min="0" />
                </div>
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label>Availability</label>
                  <select value={profile.availability} onChange={(e) => handleInputChange('availability', e.target.value)} className="form-select">
                    <option value="Available">Available</option>
                    <option value="Unavailable">Unavailable</option>
                  </select>
                </div>

                <div className="skills-edit">
                  <h4>Skills & Expertise ({profile.skills.length})</h4>
                  <div className="selected-skills-list">
                    {profile.skills.map((skill, index) => (
                      <div key={index} className="selected-skill">
                        <span>{skill.name}</span>
                        <select
                          value={skill.experience_level}
                          onChange={(e) => {
                            const newSkills = [...profile.skills];
                            newSkills[index].experience_level = e.target.value;
                            setProfile({ ...profile, skills: newSkills });
                          }}
                        >
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="expert">Expert</option>
                        </select>
                        <button type="button" onClick={() => handleSkillToggle(skill.name)}>×</button>
                      </div>
                    ))}
                  </div>
                  <div className="available-skills-list" style={{ marginTop: '15px' }}>
                    <h5>Add Skills</h5>
                    <div className="skills-grid">
                      {availableSkills.filter(s => !profile.skills.some(ps => ps.name === s)).map(s => (
                        <button key={s} type="button" className="skill-add-btn" onClick={() => handleSkillToggle(s)}>+ {s}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="edit-section">
                <h2><Settings size={20} /> Profile Status</h2>
                <div className="status-info">
                  <div className="status-item"><strong>Status:</strong> <span className={`status ${profile.verificationStatus}`}>{profile.verificationStatus}</span></div>
                  <div className="status-item"><strong>Joined:</strong> <span>{new Date(profile.joinDate).toLocaleDateString()}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="edit-actions">
          <button onClick={() => navigate('/talent/profile')} className="cancel-button" disabled={saving}><X size={16} /> Cancel</button>
          <button onClick={handleSave} className="save-button" disabled={saving}><Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}</button>
        </div>

        {/* Location Selection Modal */}
        {showLocationModal && profile && (
          <div className="location-modal-overlay">
            <div className="location-modal-container">
              <div className="location-modal-header">
                <h3>Select Your Service Location</h3>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowLocationModal(false)}
                >
                  <X size={24} />
                </button>
              </div>
              <div className="location-modal-body">
                <ServiceLocationMap
                  isEditing={true}
                  onConfirm={handleLocationConfirm}
                  initialCoordinates={profile.latitude && profile.longitude ? { lat: profile.latitude, lng: profile.longitude } : undefined}
                  initialAddress={profile.address}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </MembershipGuard>
  );
};

export default TalentProfileEdit;